import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Download, Upload, History, AlertTriangle, CheckCircle2,
  ArrowRight, Smartphone, ChevronRight, Printer, X,
} from "lucide-react";
import FakturPrint from "../components/faktur/FakturPrint";
import PemilihTemplate from "../components/faktur/PemilihTemplate";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";
const hariIni = () => hariIniISO();

const STATUS_QTY = {
  cocok: { l: "Cocok", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  selisih: { l: "Selisih", c: "bg-amber-50 text-amber-700 border-amber-200" },
  hanya_sistem: { l: "Hanya Sistem", c: "bg-rose-50 text-rose-700 border-rose-200" },
  hanya_fisik: { l: "Hanya Fisik", c: "bg-violet-50 text-violet-700 border-violet-200" },
  belum_dihitung: { l: "Belum Dihitung", c: "bg-slate-100 text-slate-600 border-slate-200" },
};

const STATUS_IMEI = {
  cocok: { l: "Cocok", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  hanya_sistem: { l: "Hanya di Sistem", c: "bg-rose-50 text-rose-700 border-rose-200" },
  hanya_fisik: { l: "Hanya di Fisik", c: "bg-violet-50 text-violet-700 border-violet-200" },
};

export default function OpnameBlind() {
  const { can } = useAuth();
  const bolehLihat = can("inventory_opname", "view");
  const bolehBuat = can("inventory_opname", "create");
  // Setiap langkah di halaman ini butuh gudang tujuan — endpoint /inventory/warehouses
  // memakai izin inventory_gudang yang berbeda dari inventory_opname yang menggerbangi
  // menu ini. Tanpa penjagaan ini, dropdown gudang kosong dan setiap aksi gagal 403.
  const bolehGudang = can("inventory_gudang", "view");
  // Daftar template cetak (menu Template Faktur & Struk) memakai izin modul
  // "faktur" — terpisah dari inventory_opname yang menggerbangi halaman ini.
  // Tanpa izin ini, pemilih template dilewati dan backend memilih template
  // default seperti biasa (perilaku lama, tetap jalan).
  const bolehFaktur = can("faktur", "view");

  const [gudang, setGudang] = useState([]);
  const [loadingGudang, setLoadingGudang] = useState(true);
  const [riwayat, setRiwayat] = useState([]);
  const [loadingRiwayat, setLoadingRiwayat] = useState(true);

  const [params, setParams] = useState({ warehouse_id: "", brand: "", cabang: "" });
  const [unduhBusy, setUnduhBusy] = useState(false);

  // Opsi dropdown Brand & Cabang (permintaan owner 26 Agu 2026 poin 1-2).
  // Dulu kolom ketik bebas: salah ketik satu huruf tidak ditolak, tidak
  // berbunyi, dan menyaring nol produk — template keluar kosong tanpa sebab
  // yang kelihatan.
  const [opsi, setOpsi] = useState({ brand: [], cabang: [] });

  // Permintaan owner poin 5. Default TRUE sesuai permintaan; bisa dimatikan
  // untuk opname yang dikerjakan bertahap, di mana "belum dihitung" yang jujur
  // lebih berguna daripada nol yang mengarang temuan barang hilang.
  const [kosongNol, setKosongNol] = useState(true);

  const [uploadMeta, setUploadMeta] = useState({ tanggal: hariIni(), catatan: "" });
  const [file, setFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  const [run, setRun] = useState(null); // dokumen run yang sedang ditampilkan (hasil unggah / klik riwayat)
  // "Ajukan hilang" per baris IMEI `hanya_sistem` (brief 4 Sep 2026 P14):
  // membuat pengajuan penyesuaian unit (PSU) yang lalu diputuskan pemutus
  // opname — bukan mengubah unit langsung. Gerbangnya `inventory_opname.create`,
  // kunci yang sama dengan POST /inventory/penyesuaian-unit. `psuDiajukan`
  // menyembunyikan tombol yang sudah ditekan supaya tidak diajukan dua kali.
  const [psuBusy, setPsuBusy] = useState(null);
  const [psuDiajukan, setPsuDiajukan] = useState({});
  const [jadikanBusy, setJadikanBusy] = useState(false);
  const reqSeq = useRef(0); // penjaga respons basi — klik riwayat beruntun tidak boleh saling menimpa

  // Cetak Berita Acara Penyesuaian Stok (Inventory #28) — {runId, step,
  // templates, templateId, loading, data} payload GET /faktur/data/adjustment/
  // {opname_id}, dirender lewat FakturPrint di modal terpisah. step:
  // "memuat_template" | "pilih_template" | "kosong" | "preview".
  const [cetak, setCetak] = useState(null);
  // Pengurutan tabel (client-side, lihat components/ui/sort.jsx) — urutan
  // bawaan tetap seperti sebelumnya. `riwayatSort` mengurutkan HANYA baris
  // yang sudah termuat — backend `/opname-blind/riwayat` default 100, maks 500.
  const barisSort = useSortState(null, "asc");
  const imeiSort = useSortState(null, "asc");
  const riwayatSort = useSortState(null, "asc");
  useEffect(() => {
    if (!bolehGudang) { setLoadingGudang(false); return; }
    let batal = false;
    (async () => {
      try {
        const r = await api.get("/inventory/warehouses");
        if (!batal) {
          const items = r.data.items || [];
          setGudang(items);
          if (items[0]) setParams(p => ({ ...p, warehouse_id: p.warehouse_id || items[0].id }));
        }
      } catch (e) {
        if (!batal) toast.error(e?.response?.data?.detail || "Gagal memuat daftar gudang");
      } finally { if (!batal) setLoadingGudang(false); }
    })();
    return () => { batal = true; };
  }, [bolehGudang]);

  const muatRiwayat = async () => {
    if (!bolehLihat) { setLoadingRiwayat(false); return; }
    setLoadingRiwayat(true);
    try {
      const r = await api.get("/inventory/opname-blind/riwayat");
      setRiwayat(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat riwayat blind opname");
    } finally { setLoadingRiwayat(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { muatRiwayat(); }, [bolehLihat]);

  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const r = await api.get("/inventory/opname-blind/opsi");
        if (!batal) setOpsi({ brand: r.data?.brand || [], cabang: r.data?.cabang || [] });
      } catch { /* dropdown kosong = "semua"; halaman tetap jalan */ }
    })();
    return () => { batal = true; };
  }, []);

  const ajukanHilang = async (u) => {
    if (!run?.warehouse_id) return;
    setPsuBusy(u.identifier);
    try {
      const r = await api.post("/inventory/penyesuaian-unit", {
        warehouse_id: run.warehouse_id, tindakan: "hilang", identifiers: [u.identifier],
        alasan: `Blind Opname ${run.code || run.id}: tidak ditemukan saat hitung fisik`,
      });
      setPsuDiajukan(m => ({ ...m, [u.identifier]: r.data?.item?.code || "diajukan" }));
      toast.success(r.data?.message || "Pengajuan hilang disimpan — menunggu keputusan pemutus opname");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengajukan unit hilang");
    } finally { setPsuBusy(null); }
  };

  const muatDetail = async (runId) => {
    const seq = ++reqSeq.current;
    try {
      const r = await api.get(`/inventory/opname-blind/${runId}`);
      if (seq === reqSeq.current) setRun(r.data);
    } catch (e) {
      if (seq === reqSeq.current) toast.error(e?.response?.data?.detail || "Gagal memuat detail run opname");
    }
  };

  const unduhTemplate = async () => {
    if (!params.warehouse_id) { toast.error("Pilih gudang dulu"); return; }
    setUnduhBusy(true);
    try {
      const token = localStorage.getItem("hris_token");
      const qs = new URLSearchParams({ warehouse_id: params.warehouse_id });
      if (params.brand.trim()) qs.set("brand", params.brand.trim());
      if (params.cabang.trim()) qs.set("cabang", params.cabang.trim());
      const res = await fetch(`${API}/inventory/opname-blind/template?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let pesan = "Gagal mengunduh lembar hitung";
        try { const j = await res.json(); if (j?.detail) pesan = j.detail; } catch { /* abaikan */ }
        throw new Error(pesan);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      const namaGudang = gudang.find(g => g.id === params.warehouse_id)?.code || params.warehouse_id;
      a.download = `BlindOpname_${namaGudang}_${hariIni()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
      toast.success("Lembar hitung diunduh — isi Qty Fisik lalu unggah hasilnya di bawah");
    } catch (e) {
      toast.error(e?.message || "Gagal mengunduh lembar hitung");
    } finally { setUnduhBusy(false); }
  };

  const unggahHasil = async () => {
    if (!params.warehouse_id) { toast.error("Pilih gudang dulu"); return; }
    if (!file) { toast.error("Pilih file hasil hitung fisik (.xlsx atau .pdf) dulu"); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("warehouse_id", params.warehouse_id);
      if (params.brand.trim()) fd.append("brand", params.brand.trim());
      if (params.cabang.trim()) fd.append("cabang", params.cabang.trim());
      fd.append("tanggal", uploadMeta.tanggal);
      fd.append("catatan", uploadMeta.catatan);
      fd.append("kosong_dianggap_nol", kosongNol ? "true" : "false");
      fd.append("file", file);
      const r = await api.post("/inventory/opname-blind/unggah", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Memo yang gagal terbit TIDAK boleh lewat sebagai sukses biasa — Bos
      // mengandalkan memo itu sebagai dokumen resmi selisih.
      if (r.data.memo_gagal) toast.error(r.data.message, { duration: 10000 });
      else toast.success(r.data.message || "Blind opname tercocokkan");
      setFile(null);
      await muatDetail(r.data.run_id);
      await muatRiwayat();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah hasil blind opname");
    } finally { setUploadBusy(false); }
  };

  const jadikanOpname = async () => {
    if (!run) return;
    const jumlahSelisih = run.ringkasan?.selisih || 0;
    if (!window.confirm(
      `Jadikan ${jumlahSelisih} SKU berselisih sebagai pengajuan opname resmi?\n\n` +
      "AKIBATNYA: pengajuan akan masuk ke antrean approval di Finance > Persediaan. " +
      "Stok BELUM berubah sekarang — baru berubah SETELAH diverifikasi & disetujui di sana. " +
      "SKU ber-IMEI akan dilewati (selesaikan lewat menu Inventory > Unit)."
    )) return;
    setJadikanBusy(true);
    try {
      const r = await api.post(`/inventory/opname-blind/${run.id}/jadikan-opname`);
      toast.success(r.data.message || "Pengajuan opname dibuat");
      await muatDetail(run.id);
      await muatRiwayat();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjadikan pengajuan opname");
    } finally { setJadikanBusy(false); }
  };

  // Hanya baris berstatus "selisih"/"hanya_sistem" yang punya angka selisih numerik dan
  // ikut dicetak backend (lihat komentar `data_faktur_adjustment` di faktur.py) — tombol
  // cetak cuma masuk akal kalau salah satu dari keduanya ada. `ringkasan.selisih` saja
  // TIDAK cukup karena tidak menghitung baris "hanya_sistem".
  const punyaSelisihUntukCetak = (ringkasan) =>
    ((ringkasan?.selisih || 0) + (ringkasan?.hanya_sistem || 0)) > 0;

  const bukaCetakAdjustment = async (runId, warehouseId, e) => {
    e?.stopPropagation(); // baris riwayat juga punya onClick sendiri (muatDetail)
    setCetak({ runId, step: "memuat_template", templates: [], templateId: null, loading: true, data: null });
    if (!bolehFaktur) { await muatDokumenAdjustment(runId, null); return; }
    try {
      // warehouse_id (brief bos): hanya tampilkan template "adjustment" yang
      // memang diizinkan untuk gudang tempat opname ini dijalankan — lihat
      // `_template_allows_warehouse` di faktur.py.
      const { data } = await api.get("/faktur/templates", { params: { jenis: "adjustment", warehouse_id: warehouseId || undefined } });
      const daftar = data?.items || [];
      if (daftar.length === 0) {
        setCetak({ runId, step: "kosong", templates: [], loading: false, data: null });
        return;
      }
      if (daftar.length === 1) { await muatDokumenAdjustment(runId, daftar[0]); return; }
      const def = daftar.find(t => t.is_default) || daftar[0];
      setCetak({ runId, step: "pilih_template", templates: daftar, templateId: def.id, loading: false, data: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat daftar template cetak");
      setCetak(null);
    }
  };

  const muatDokumenAdjustment = async (runId, template) => {
    setCetak(m => ({ ...(m || { runId }), runId, step: "preview", loading: true, data: null }));
    try {
      const { data } = await api.get(`/faktur/data/adjustment/${runId}`, {
        params: template ? { template_id: template.id } : {},
      });
      setCetak(m => (m ? { ...m, step: "preview", loading: false, data } : m));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat berita acara penyesuaian stok");
      setCetak(null);
    }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8" data-testid="opname-blind">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Akun Anda belum diberi izin melihat Blind Stock Opname. Minta admin mencentang modul
          "Inventory · Stok Opname" pada role Anda.
        </div>
      </div>
    );
  }

  const r = run?.ringkasan;

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="opname-blind">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Blind Stock Opname</h1>
        <p className="text-sm text-slate-600 mt-1">
          Staf menghitung fisik tanpa melihat stok sistem — itulah gunanya "blind". Hasilnya dicocokkan
          otomatis, lalu selisihnya bisa dijadikan pengajuan opname resmi.
        </p>
      </div>

      {!bolehGudang && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Akun Anda belum punya izin modul Gudang, jadi gudang tidak bisa dipilih dan blind opname
          tidak bisa dijalankan dari sini.
        </div>
      )}

      {bolehGudang && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ===== Langkah 1: Unduh lembar hitung ===== */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold grid place-items-center">1</span>
                <h2 className="font-display font-bold text-slate-900">Unduh Lembar Hitung</h2>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                Lembar ini SENGAJA tidak menampilkan qty sistem — staf mengisi hitungan fisik tanpa
                melihat stok sistem, supaya hasilnya tidak bias ke angka yang sudah terlihat duluan.
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Gudang</label>
                <select value={params.warehouse_id} disabled={loadingGudang} data-testid="ob-gudang"
                        onChange={e => setParams(p => ({ ...p, warehouse_id: e.target.value }))} className={inp}>
                  <option value="">— pilih gudang —</option>
                  {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Brand (opsional)</label>
                  <select value={params.brand} data-testid="ob-brand"
                          onChange={e => setParams(p => ({ ...p, brand: e.target.value }))} className={inp}>
                    <option value="">Semua brand</option>
                    {opsi.brand.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Cabang (opsional)</label>
                  <select value={params.cabang} data-testid="ob-cabang"
                          onChange={e => setParams(p => ({ ...p, cabang: e.target.value }))} className={inp}>
                    <option value="">Semua cabang</option>
                    {opsi.cabang.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={unduhTemplate} disabled={unduhBusy || !params.warehouse_id} data-testid="ob-unduh"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                <Download className="w-4 h-4" /> {unduhBusy ? "Menyiapkan..." : "Unduh Lembar Hitung (tanpa qty sistem)"}
              </button>
            </div>

            {/* ===== Langkah 2: Unggah hasil ===== */}
            <div className={`bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3 ${!bolehBuat ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold grid place-items-center">2</span>
                <h2 className="font-display font-bold text-slate-900">Unggah Hasil Hitung Fisik</h2>
              </div>
              {!bolehBuat ? (
                <p className="text-sm text-slate-500">
                  Akun Anda hanya bisa melihat, tidak bisa mengunggah hasil hitung fisik.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500">
                    Gunakan gudang yang sama seperti langkah 1 di atas — file yang diunduh terkunci ke gudang itu.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal opname</label>
                      <DatePicker value={uploadMeta.tanggal} data-testid="ob-tanggal"
                             onChange={v => setUploadMeta(m => ({ ...m, tanggal: v }))} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">File hasil (.xlsx / .pdf)</label>
                      <input type="file" accept=".xlsx,.pdf" data-testid="ob-file"
                             onChange={e => setFile(e.target.files?.[0] || null)}
                             className="w-full h-10 px-2 border border-slate-200 rounded-lg text-xs file:mr-2 file:h-full file:px-2 file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Catatan (opsional)</label>
                    <input value={uploadMeta.catatan} data-testid="ob-catatan"
                           onChange={e => setUploadMeta(m => ({ ...m, catatan: e.target.value }))} className={inp} />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-600 mb-2 cursor-pointer">
                    <input type="checkbox" checked={kosongNol} data-testid="ob-kosong-nol"
                           onChange={e => setKosongNol(e.target.checked)} className="mt-0.5" />
                    <span>
                      Baca Qty yang dibiarkan kosong sebagai <b>0</b> (barang dianggap habis).
                      Matikan kalau opname ini dikerjakan bertahap dan masih ada baris yang
                      belum sempat dihitung — kalau dibiarkan menyala, baris itu akan muncul
                      sebagai barang hilang.
                    </span>
                  </label>
                  <button onClick={unggahHasil} disabled={uploadBusy || !params.warehouse_id || !file} data-testid="ob-unggah"
                          className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                    <Upload className="w-4 h-4" /> {uploadBusy ? "Mencocokkan..." : "Unggah & Cocokkan"}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ===== Hasil pencocokan ===== */}
          {run && r && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4" data-testid="ob-hasil">
              <div className="flex items-start justify-between flex-col md:flex-row gap-2">
                <div>
                  <h2 className="font-display font-bold text-slate-900">
                    Hasil Pencocokan — {run.code}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {run.warehouse_nama} · {run.tanggal}
                    {run.brand_filter ? ` · brand ${run.brand_filter}` : ""}
                    {run.cabang_filter ? ` · cabang ${run.cabang_filter}` : ""}
                  </p>
                </div>
                {/* ===== Langkah 3: Jadikan pengajuan opname ===== */}
                {bolehBuat && r.selisih > 0 && (
                  run.opname_dibuat ? (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {(run.opname_diajukan || []).length} SKU sudah diajukan ke{" "}
                      <Link to="/finance/persediaan" className="underline font-medium">Finance &gt; Persediaan</Link>
                    </div>
                  ) : (
                    <button onClick={jadikanOpname} disabled={jadikanBusy} data-testid="ob-jadikan"
                            className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                      <ArrowRight className="w-4 h-4" /> {jadikanBusy ? "Memproses..." : "Jadikan Pengajuan Opname"}
                    </button>
                  )
                )}
              </div>

              {run.ai_ringkasan && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                  {run.ai_ringkasan}
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KartuRingkasan label="Cocok" nilai={r.cocok} tone="text-emerald-700" bg="bg-emerald-50 border-emerald-200" />
                <KartuRingkasan label="Selisih" nilai={r.selisih} tone="text-amber-700" bg="bg-amber-50 border-amber-200" />
                <KartuRingkasan label="Hanya Sistem" nilai={r.hanya_sistem} tone="text-rose-700" bg="bg-rose-50 border-rose-200" />
                <KartuRingkasan label="Hanya Fisik" nilai={r.hanya_fisik} tone="text-violet-700" bg="bg-violet-50 border-violet-200" />
                <KartuRingkasan label="Nilai Bersih" nilai={idr(r.nilai_selisih_total)} tone="text-slate-900" bg="bg-slate-50 border-slate-200" />
              </div>

              {/* Plus & minus DIPISAH (permintaan owner poin 4). "Nilai Bersih" di
                  atas sendirian menyesatkan: barang lebih Rp 10jt dan barang hilang
                  Rp 10jt menghasilkan Rp 0, seolah tidak ada apa-apa. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ob-selisih-arah">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">
                    Selisih PLUS — fisik lebih, sistem kurang
                  </p>
                  <p className="text-xl font-display font-bold text-emerald-800 mt-1">
                    {idr(r.nilai_selisih_plus || 0)}
                  </p>
                  <p className="text-xs text-emerald-700">
                    {r.sku_selisih_plus || 0} SKU · {r.qty_selisih_plus || 0} unit
                  </p>
                </div>
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold">
                    Selisih MINUS — fisik kurang, sistem lebih
                  </p>
                  <p className="text-xl font-display font-bold text-rose-800 mt-1">
                    {idr(r.nilai_selisih_minus || 0)}
                  </p>
                  <p className="text-xs text-rose-700">
                    {r.sku_selisih_minus || 0} SKU · {r.qty_selisih_minus || 0} unit
                    {r.hanya_sistem ? ` · termasuk ${r.hanya_sistem} SKU yang tidak ditemukan sama sekali` : ""}
                  </p>
                </div>
              </div>

              {(r.baris_kosong_dianggap_nol || 0) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900"
                     data-testid="ob-peringatan-kosong">
                  <b>{r.baris_kosong_dianggap_nol} baris qty-nya dibiarkan kosong</b> dan dibaca sebagai 0,
                  jadi muncul di atas sebagai barang kurang. Pastikan dulu barangnya memang habis —
                  bukan belum sempat dihitung — sebelum selisihnya dijadikan pengajuan opname.
                </div>
              )}

              {run.memo_nomor && (
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-900"
                     data-testid="ob-memo">
                  Internal Memo <b>{run.memo_nomor}</b> diterbitkan otomatis untuk selisih ini.
                </div>
              )}

              {run.opname_dilewati && run.opname_dilewati.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
                  <p className="font-semibold">SKU ber-IMEI dilewati saat dijadikan pengajuan opname:</p>
                  {run.opname_dilewati.map(d => (
                    <p key={d.sku}>{d.sku} — {d.alasan}</p>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr className="text-left">
                      <SortTh label="SKU" sortKeyName="sku" sortKey={barisSort.sortKey} sortDir={barisSort.sortDir} onSort={barisSort.toggle} testid="ob-baris-sort-sku" />
                      <SortTh label="Nama" sortKeyName="nama" sortKey={barisSort.sortKey} sortDir={barisSort.sortDir} onSort={barisSort.toggle} testid="ob-baris-sort-nama" />
                      <SortTh label="Qty Fisik" sortKeyName="qty_fisik" sortKey={barisSort.sortKey} sortDir={barisSort.sortDir} onSort={barisSort.toggle} align="right" testid="ob-baris-sort-fisik" />
                      <SortTh label="Qty Sistem" sortKeyName="qty_sistem" sortKey={barisSort.sortKey} sortDir={barisSort.sortDir} onSort={barisSort.toggle} align="right" testid="ob-baris-sort-sistem" />
                      <SortTh label="Selisih" sortKeyName="selisih" sortKey={barisSort.sortKey} sortDir={barisSort.sortDir} onSort={barisSort.toggle} align="right" testid="ob-baris-sort-selisih" />
                      <SortTh label="Nilai Selisih" sortKeyName="nilai_selisih" sortKey={barisSort.sortKey} sortDir={barisSort.sortDir} onSort={barisSort.toggle} align="right" testid="ob-baris-sort-nilai" />
                      <th className="px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(run.baris || []).length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Belum ada baris</td></tr>
                    ) : sortRows(run.baris, barisSort.sortKey, barisSort.sortDir).map(b => {
                      const st = STATUS_QTY[b.status] || STATUS_QTY.belum_dihitung;
                      return (
                        <tr key={b.sku} data-testid={`ob-baris-${b.sku}`} className="hover:bg-slate-50/60">
                          <td className="px-3 py-2 font-mono text-xs">{b.sku}</td>
                          <td className="px-3 py-2">
                            {b.nama}
                            {b.has_imei && <Smartphone className="w-3 h-3 text-slate-400 inline ml-1" />}
                          </td>
                          <td className="px-3 py-2 text-right">{b.qty_fisik ?? "—"}</td>
                          <td className="px-3 py-2 text-right">{b.qty_sistem}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${b.selisih ? (b.selisih > 0 ? "text-emerald-700" : "text-rose-700") : "text-slate-400"}`}>
                            {b.selisih ?? "—"}
                            {/* Arah dinyatakan dengan KATA, bukan hanya tanda minus —
                                pembaca tidak perlu mengingat konvensinya. */}
                            {b.arah_selisih && b.arah_selisih !== "nol" && b.arah_selisih !== "tidak_diketahui" && (
                              <span className="block text-[10px] font-normal">
                                {b.arah_selisih === "plus" ? "plus (fisik lebih)" : "minus (fisik kurang)"}
                              </span>
                            )}
                            {b.kosong_dibaca_nol && (
                              <span className="block text-[10px] font-normal text-amber-700">qty kosong → 0</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{idr(b.nilai_selisih)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[11px] px-2 py-0.5 rounded border ${st.c}`}>{st.l}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(run.imei_baris || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                    <Smartphone className="w-3.5 h-3.5" /> Kecocokan IMEI / SN
                  </p>
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr className="text-left">
                          <SortTh label="IMEI / SN" sortKeyName="identifier" sortKey={imeiSort.sortKey} sortDir={imeiSort.sortDir} onSort={imeiSort.toggle} testid="ob-imei-sort-identifier" />
                          <SortTh label="SKU" sortKeyName="sku" sortKey={imeiSort.sortKey} sortDir={imeiSort.sortDir} onSort={imeiSort.toggle} testid="ob-imei-sort-sku" />
                          <SortTh label="Nama" sortKeyName="nama" sortKey={imeiSort.sortKey} sortDir={imeiSort.sortDir} onSort={imeiSort.toggle} testid="ob-imei-sort-nama" />
                          <th className="px-3 py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortRows(run.imei_baris, imeiSort.sortKey, imeiSort.sortDir).map(u => {
                          const st = STATUS_IMEI[u.status] || STATUS_IMEI.hanya_fisik;
                          return (
                            <tr key={u.identifier} data-testid={`ob-imei-${u.identifier}`} className="hover:bg-slate-50/60">
                              <td className="px-3 py-2 font-mono text-xs">{u.identifier}</td>
                              <td className="px-3 py-2 font-mono text-xs">{u.sku || "—"}</td>
                              <td className="px-3 py-2">{u.nama || "—"}</td>
                              <td className="px-3 py-2">
                                <span className={`text-[11px] px-2 py-0.5 rounded border ${st.c}`}>{st.l}</span>
                                {u.status_unit === "rusak" && (
                                  <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">rusak</span>
                                )}
                                {u.status === "hanya_sistem" && bolehBuat && run?.warehouse_id && (
                                  psuDiajukan[u.identifier]
                                    ? <span className="ml-2 text-[11px] text-emerald-700">diajukan {psuDiajukan[u.identifier]}</span>
                                    : <button onClick={() => ajukanHilang(u)} disabled={psuBusy === u.identifier}
                                              data-testid={`ob-imei-ajukan-hilang-${u.identifier}`}
                                              className="ml-2 text-[11px] px-2 py-0.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                                        {psuBusy === u.identifier ? "..." : "Ajukan hilang"}
                                      </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== Riwayat ===== */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="ob-riwayat">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <h2 className="font-display font-bold text-slate-900 text-sm">Riwayat Blind Opname</h2>
            </div>
            <div className="overflow-x-auto">
              {loadingRiwayat ? (
                <LoadingState loading data-testid="ob-riwayat-loading" />
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr className="text-left">
                      <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} testid="ob-riwayat-sort-tanggal" />
                      <SortTh label="Gudang" sortKeyName="warehouse_nama" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} testid="ob-riwayat-sort-gudang" />
                      <SortTh label="Cocok" sortKeyName="cocok" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="ob-riwayat-sort-cocok" />
                      <SortTh label="Selisih" sortKeyName="selisih" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="ob-riwayat-sort-selisih" />
                      <SortTh label="Nilai Selisih" sortKeyName="nilai_selisih_total" sortKey={riwayatSort.sortKey} sortDir={riwayatSort.sortDir} onSort={riwayatSort.toggle} align="right" testid="ob-riwayat-sort-nilai" />
                      <th className="px-3 py-2.5 font-semibold">Status</th>
                      <th className="px-3 py-2.5 font-semibold text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {riwayat.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Belum ada riwayat blind opname</td></tr>
                    ) : sortRows(riwayat, riwayatSort.sortKey, riwayatSort.sortDir, {
                        cocok: r => r.ringkasan?.cocok ?? 0,
                        selisih: r => r.ringkasan?.selisih ?? 0,
                        nilai_selisih_total: r => r.ringkasan?.nilai_selisih_total ?? 0,
                      }).map(row => (
                      <tr key={row.id} onClick={() => muatDetail(row.id)} data-testid={`ob-riwayat-${row.id}`}
                          className="hover:bg-slate-50/60 cursor-pointer">
                        <td className="px-3 py-2.5">
                          <p className="font-mono text-[11px] text-slate-500">{row.code}</p>
                          <p className="text-slate-800">{row.tanggal}</p>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">{row.warehouse_nama}</td>
                        <td className="px-3 py-2.5 text-right text-emerald-700">{row.ringkasan?.cocok ?? 0}</td>
                        <td className="px-3 py-2.5 text-right text-amber-700">{row.ringkasan?.selisih ?? 0}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{idr(row.ringkasan?.nilai_selisih_total)}</td>
                        <td className="px-3 py-2.5">
                          {row.opname_dibuat ? (
                            <span className="text-[11px] px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">Sudah diajukan</span>
                          ) : (
                            <span className="text-[11px] px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">Belum diajukan</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {punyaSelisihUntukCetak(row.ringkasan) && (
                              <button onClick={(e) => bukaCetakAdjustment(row.id, row.warehouse_id, e)} data-testid={`ob-cetak-${row.id}`}
                                      title="Cetak Berita Acara Penyesuaian Stok"
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                                <Printer className="w-4 h-4" />
                              </button>
                            )}
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ===== Cetak Berita Acara Penyesuaian Stok (Inventory #28) ===== */}
          {cetak && (
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
                 onClick={() => setCetak(null)}>
              <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
                   data-testid="ob-cetak-modal">
                <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                  <h3 className="font-display font-semibold inline-flex items-center gap-2">
                    <Printer className="w-4 h-4" /> Cetak Berita Acara Penyesuaian Stok
                  </h3>
                  <button onClick={() => setCetak(null)} className="p-2 hover:bg-white/20 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-6">
                  {cetak.step === "pilih_template" ? (
                    <div className="space-y-4">
                      <PemilihTemplate templates={cetak.templates} value={cetak.templateId}
                        onChange={(id) => setCetak(m => ({ ...m, templateId: id }))} jenisLabel="berita acara penyesuaian stok"
                        testidPrefix="ob-cetak-tpl" />
                      <div className="flex justify-end">
                        <button onClick={() => muatDokumenAdjustment(cetak.runId, cetak.templates.find(t => t.id === cetak.templateId))}
                          data-testid="ob-cetak-tpl-lanjut"
                          className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                          Lanjutkan
                        </button>
                      </div>
                    </div>
                  ) : cetak.step === "kosong" ? (
                    <p className="text-sm text-slate-500 text-center py-8" data-testid="ob-cetak-tpl-kosong">
                      Belum ada template berita acara penyesuaian stok. Buat dulu di menu Template Faktur &amp; Struk.
                    </p>
                  ) : cetak.loading || !cetak.data ? (
                    <LoadingState loading jumlahKartu={1} data-testid="ob-cetak-loading" />
                  ) : (
                    <FakturPrint
                      data={adaptAdjustmentUntukCetak(cetak.data.adjustment, cetak.data.items)}
                      template={cetak.data.template}
                      noFaktur={cetak.data.no_faktur}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Adaptor LOKAL (pola sama dengan `adaptReturJualUntukCetak`/`adaptReturBeliUntukCetak`
 * di Retur.jsx) — payload `GET /faktur/data/adjustment/{id}` membungkus dokumen dalam key
 * "adjustment" + baris berselisih di "items" (bentuk `opname_blind_runs`, BUKAN skema
 * sale/po/transfer yang dikenali `normalisasiEntitas`/`normalisasiItems` di FakturPrint.jsx),
 * jadi tanpa adaptor ini komponen itu akan salah membaca payload mentah. Field per-baris
 * qty_sistem/qty_fisik tidak punya kolom sendiri di tabel generik FakturPrint — dititipkan
 * ke `nama_barang` supaya tetap terbaca di dokumen cetak.
 */
function adaptAdjustmentUntukCetak(adjustment, items) {
  const baris = items || [];
  return {
    code: adjustment?.code,
    nama_pelanggan: `Penyesuaian Stok — ${adjustment?.warehouse_nama || "-"}`,
    tanggal: adjustment?.tanggal,
    warehouse_nama: adjustment?.warehouse_nama,
    cabang: adjustment?.cabang_filter || "",
    items: baris.map((it) => ({
      nama_barang: `${it.nama || it.sku || "-"} (Sistem ${it.qty_sistem ?? "-"} → Fisik ${it.qty_fisik ?? "-"})`,
      sku: it.sku || "",
      qty: it.selisih,
      nilai: Number(it.nilai_selisih) || 0,
    })),
    total_harga: baris.reduce((a, it) => a + (Number(it.nilai_selisih) || 0), 0),
    catatan: [
      adjustment?.catatan,
      ...baris.map((it) => (it.catatan ? `${it.nama || it.sku}: ${it.catatan}` : "")).filter(Boolean),
    ].filter(Boolean).join(" · "),
  };
}

function KartuRingkasan({ label, nilai, tone, bg }) {
  return (
    <div className={`rounded-lg border p-3 ${bg}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-lg font-bold mt-1 ${tone}`}>{nilai}</p>
    </div>
  );
}

